import { CopyButton } from "@/components/ui/copy-button";
import { StatusDot } from "@/components/ui/status-dot";
import { formatShortDistance } from "@/lib/utils/date-utils";
import type { McpSessionRecord } from "@/stores/mcpSessionStore";
import type { ReactNode } from "react";
import { useMemo } from "react";
import type { Deployment } from "../../../../schemas";
import { McpConnectionStatus } from "../McpConnectionStatus";
import { TelemetrySummary } from "./TelemetrySummary";

import { TabLinkInline } from "./TabLinkInline";

interface ProjectStatusSectionProps {
  projectId: string;
  deployment: Deployment | null;
  deploymentUrl?: string;
  session?: McpSessionRecord;
}

const DEPLOYMENT_STATUS_COLOR_MAP = {
  ready: "bg-success",
  unavailable: "bg-warning",
} as const;

const DEPLOYMENT_STATUS_LABEL_MAP = {
  ready: "Ready",
  unavailable: "Unavailable",
} as const;

export function ProjectStatusSection({
  projectId,
  deployment,
  deploymentUrl,
  session,
}: ProjectStatusSectionProps) {
  const sessionStatus = session?.status ?? "idle";

  const deploymentMetadata = useMemo<ReactNode>(() => {
    if (!deployment) {
      return (
        <TabLinkInline projectId={projectId} tab="deploys">
          Deploy your project to publish a live URL.
        </TabLinkInline>
      );
    }

    const updatedAtValue = deployment.updatedAt
      ? new Date(deployment.updatedAt)
      : undefined;

    let relative = "recently";
    let updatedAtLabel: string | undefined;

    if (updatedAtValue && !Number.isNaN(updatedAtValue.getTime())) {
      relative = formatShortDistance(updatedAtValue);
      updatedAtLabel = updatedAtValue.toUTCString();
    }

    switch (deployment.status) {
      case "complete":
        return (
          <span>
            Last deploy <span aria-label={updatedAtLabel}>{relative}</span>
          </span>
        );
      case "error":
        return (
          <span className="text-destructive">
            Last deploy failed {relative}{" "}
            <TabLinkInline projectId={projectId} tab="deploys">
              View deploy
            </TabLinkInline>
          </span>
        );
      case "executing":
        return <span>Deploying (started {relative})</span>;
      case "pending":
        return <span>Deploy queued {relative}</span>;
      case "cancelled":
        return (
          <span>
            Last deploy cancelled {relative}{" "}
            <TabLinkInline projectId={projectId} tab="deploys">
              Review deploys
            </TabLinkInline>
          </span>
        );
      default:
        return <span>Last deployment update {relative}</span>;
    }
  }, [deployment, projectId]);

  const lastConnectedAt = session?.lastConnectedAt;
  const sessionError = session?.error;
  const deploymentStatusValue = deployment?.status;

  const sessionDescription = useMemo(() => {
    switch (sessionStatus) {
      case "connected": {
        if (!lastConnectedAt) {
          return "Connection established.";
        }

        const lastConnectedDate = new Date(lastConnectedAt);

        if (Number.isNaN(lastConnectedDate.getTime())) {
          return "Connection established.";
        }

        return `Connected ${formatShortDistance(lastConnectedDate)}`;
      }
      case "connecting":
        return "Attempting to connect to the MCP server…";
      case "error":
        return sessionError
          ? sessionError
          : "Connection failed. Check deployment or credentials.";
      default:
        return deploymentStatusValue === "complete"
          ? "Open the inspector to initialize an MCP session."
          : "Deploy your project to enable MCP connectivity.";
    }
  }, [deploymentStatusValue, lastConnectedAt, sessionError, sessionStatus]);

  const deploymentStatus: keyof typeof DEPLOYMENT_STATUS_COLOR_MAP =
    deploymentUrl ? "ready" : "unavailable";
  const deploymentStatusLabel = DEPLOYMENT_STATUS_LABEL_MAP[deploymentStatus];

  return (
    <div className="flex justify-between gap-4">
      <div className="py-4">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <span>Deployed to</span>
          {deploymentUrl ? (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <a
                href={deploymentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="font-mono text-primary hover:underline"
              >
                {deploymentUrl}
              </a>
              <CopyButton value={deploymentUrl} />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Deploy your project to generate a live URL.
            </p>
          )}
        </div>
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-foreground font-medium">
            <StatusDot
              status={deploymentStatus}
              colorMap={DEPLOYMENT_STATUS_COLOR_MAP}
              ariaLabel={`Deployment status: ${deploymentStatusLabel}`}
              pulse={!deploymentUrl}
            />
            <span>{deploymentStatusLabel}</span>
          </span>
          {deploymentMetadata}
        </div>
      </div>

      <div className="flex items-end gap-6 py-4">
        <McpConnectionStatus
          status={sessionStatus}
          description={sessionDescription}
          className="max-w-xs"
        />
        <TelemetrySummary projectId={projectId} />
      </div>
    </div>
  );
}
